import { DataTableCell } from '@/_shared/components/data-table/cell.js';
import { DataTableHeaderCell } from '@/_shared/components/data-table/header/index.js';
import { DataTable } from '@/_shared/components/data-table/index.js';
import { useTsRest } from '@/_shared/context/ts-rest.js';
import { useToast } from '@/_shared/hooks/toast.js';
import { Body1, Button, Checkbox, Link as FluentUiLink, Spinner, Tooltip, tokens } from '@fluentui/react-components';
import { AddRegular, DismissCircleFilled, SendRegular, WarningFilled } from '@fluentui/react-icons';
import { Link, useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import type { PendingClaim } from '@this/shared/dist/apis/serviceaddinapi/pending-claims.js';
import { fromIso, toHumanReadable } from '@this/shared/dist/tools/datetime.js';
import { formatEur } from '@this/shared/dist/tools/number-format.js';
import { useCallback, useMemo } from 'react';
import { PendingClaimsSelectionProvider, usePendingClaimsSelection } from './_context.js';
import { useStyles } from './_styles.js';

const columnHelper = createColumnHelper<PendingClaim>();

function SelectAllHeader() {
    const { isAllSelected, clearSelection, selectAll } = usePendingClaimsSelection();
    const tsr = useTsRest();

    const { data } = tsr.pendingClaims.getPendingClaims.useQuery({
        queryKey: ['pending-claims'],
        refetchInterval: 30_000,
    });

    const selectableClaimIds = useMemo(() => {
        if (!data?.body.claims) {
            return [];
        }
        return data.body.claims
            .filter((claim) => claim.status !== 'filing' && claim.status !== 'failed' && claim.status !== 'invalid')
            .map((claim) => claim.id);
    }, [data?.body.claims]);

    const handleSelectAllChange = useCallback(() => {
        if (isAllSelected(selectableClaimIds)) {
            clearSelection();
        } else {
            selectAll(selectableClaimIds);
        }
    }, [isAllSelected, clearSelection, selectAll, selectableClaimIds]);

    return (
        <Checkbox
            checked={selectableClaimIds.length > 0 && isAllSelected(selectableClaimIds)}
            onChange={handleSelectAllChange}
            aria-label="Alle auswählen"
        />
    );
}

function SelectCell({ claimId, status }: { claimId: string; status: string }) {
    const { isSelected, toggleSelection } = usePendingClaimsSelection();

    if (status === 'filing') {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                }}
            >
                <Tooltip content="Schaden wird eingereicht" relationship="label">
                    <Spinner size="extra-tiny" aria-label="Schaden wird eingereicht" />
                </Tooltip>
            </div>
        );
    }
    if (status === 'failed') {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                }}
            >
                <Tooltip content="Einreichung fehlgeschlagen" relationship="label">
                    <DismissCircleFilled
                        style={{ color: tokens.colorStatusDangerForeground1 }}
                        aria-label="Einreichung fehlgeschlagen"
                    />
                </Tooltip>
            </div>
        );
    }
    if (status === 'invalid') {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                }}
            >
                <Tooltip content="Unvollständige Angaben" relationship="label">
                    <WarningFilled
                        style={{ color: tokens.colorStatusWarningForeground1 }}
                        aria-label="Unvollständige Angaben"
                    />
                </Tooltip>
            </div>
        );
    }
    return (
        <Checkbox
            checked={isSelected(claimId)}
            onChange={() => toggleSelection(claimId)}
            aria-label={`Schaden auswählen`}
        />
    );
}

function PendingClaimsContent() {
    const styles = useStyles();
    const tsr = useTsRest();
    const navigate = useNavigate();
    const toast = useToast();
    const { selectedIds, clearSelection } = usePendingClaimsSelection();

    const { data, isLoading, error, refetch } = tsr.pendingClaims.getPendingClaims.useQuery({
        queryKey: ['pending-claims'],
        refetchInterval: 30_000,
    });

    const fileClaimsMutation = tsr.pendingClaims.fileMultipleClaims.useMutation();

    const handleFileClaimsClick = useCallback(async () => {
        if (selectedIds.length === 0) {
            return;
        }

        try {
            const result = await fileClaimsMutation.mutateAsync({
                body: { claimIds: selectedIds },
            });

            if (result.status === 200) {
                const { filedCount, skippedCount } = result.body;

                // Clear selection and refetch data
                clearSelection();
                await refetch();

                // Show success toast
                toast(
                    'Schäden eingereicht',
                    filedCount > 0
                        ? `${filedCount} ${filedCount === 1 ? 'Schaden' : 'Schäden'} erfolgreich eingereicht${
                              skippedCount > 0 ? `, ${skippedCount} übersprungen` : ''
                          }`
                        : `Keine Schäden eingereicht (${skippedCount} übersprungen)`,
                    { intent: filedCount > 0 ? 'success' : 'warning' }
                );
            }
        } catch {
            toast('Fehler beim Einreichen der Schäden', 'Fehler beim Einreichen der Schäden', { intent: 'error' });
        }
    }, [selectedIds, fileClaimsMutation, refetch, toast, clearSelection]);

    const handleNewClaimClick = useCallback(() => {
        navigate({ to: '/new-claim' });
    }, [navigate]);

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'select',
                size: 50,
                header: () => <SelectAllHeader />,
                cell: ({ row }) => <SelectCell claimId={row.original.id} status={row.original.status} />,
            }),
            columnHelper.display({
                id: 'id',
                size: 100,
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="pending-claims"
                        table={context.table}
                        column={context.column}
                        title="ID"
                    />
                ),
                cell: ({ row }) => {
                    const claim = row.original;

                    return (
                        <DataTableCell>
                            <Link to="/new-claim/$id" params={{ id: claim.id }}>
                                <FluentUiLink as="span">{claim.id}</FluentUiLink>
                            </Link>
                        </DataTableCell>
                    );
                },
            }),
            columnHelper.accessor('contractNumber', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="pending-claims"
                        table={context.table}
                        column={context.column}
                        title="Vertrag"
                        tooltip="Vertragsnummer"
                    />
                ),
                cell: (info) => info.row.original.contractNumber || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('policyHolderName', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="pending-claims"
                        table={context.table}
                        column={context.column}
                        title="Versicherungsnehmer"
                    />
                ),
                cell: (info) => info.row.original.policyHolderName || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('claimDate', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="pending-claims"
                        table={context.table}
                        column={context.column}
                        title="Schadendatum"
                    />
                ),
                cell: (info) =>
                    info.row.original.claimDate
                        ? toHumanReadable(fromIso(info.row.original.claimDate), 'date_short_2digit')
                        : '—',
                filterFn: 'dateFilter',
                meta: {
                    filter: { type: 'date' },
                },
            }),
            columnHelper.accessor('incidentLocation', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="pending-claims"
                        table={context.table}
                        column={context.column}
                        title="Schadenadresse"
                    />
                ),
                cell: (info) => info.row.original.incidentLocation || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('claimType', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="pending-claims"
                        table={context.table}
                        column={context.column}
                        title="Schadenart"
                    />
                ),
                cell: (info) => info.row.original.claimType || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('claimAmountEur', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="pending-claims"
                        table={context.table}
                        column={context.column}
                        title="Schadenhöhe"
                    />
                ),
                cell: (info) => (info.row.original.claimAmountEur ? formatEur(info.row.original.claimAmountEur) : '—'),
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('filesCount', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="pending-claims"
                        table={context.table}
                        column={context.column}
                        title="Anzahl Dateien"
                    />
                ),
                cell: (info) => info.row.original.filesCount,
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
        ],
        []
    );

    if (isLoading) {
        return (
            <div className={styles.page} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Spinner size="large" label="Lade Schäden in Vorbereitung..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <Body1 style={{ color: 'var(--colorPaletteRedForeground1)' }}>
                    Fehler beim Laden der Daten: {error instanceof Error ? error.message : 'Unbekannter Fehler'}
                </Body1>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <DataTable<PendingClaim, any>
                id="pending-claims"
                tableLabel="Schäden in Vorbereitung"
                loading={isLoading}
                data={data?.body.claims}
                columns={columns as any}
                getItemId={(item) => item.id}
                columnTitles={{
                    contractNumber: 'Vertrag',
                    policyHolderName: 'Versicherungsnehmer',
                    claimDate: 'Schadendatum',
                    incidentLocation: 'Schadenadresse',
                    claimType: 'Schadenart',
                    claimAmountEur: 'Schadenhöhe',
                    filesCount: 'Anzahl Dateien',
                }}
                defaultSorting={[
                    {
                        id: 'claimDate',
                        desc: true,
                    },
                ]}
                customToolbarActions={
                    <>
                        <Button appearance="secondary" icon={<AddRegular />} onClick={handleNewClaimClick} size="small">
                            Schaden anlegen
                        </Button>
                        <Button
                            appearance="primary"
                            icon={<SendRegular />}
                            disabled={selectedIds.length === 0 || fileClaimsMutation.isPending}
                            onClick={handleFileClaimsClick}
                            size="small"
                        >
                            {fileClaimsMutation.isPending ? 'Wird eingereicht...' : 'Schäden einreichen'}
                        </Button>
                    </>
                }
                generateCustomToolbarCounts={(rows) => [
                    {
                        label: 'Anzahl Schäden',
                        count: rows.length,
                    },
                ]}
                dataExportFunc={(claims) => ({
                    identifier: 'Schäden_in_Vorbereitung',
                    header: [
                        [
                            'Vertrag',
                            'Versicherungsnehmer',
                            'Schadendatum',
                            'Schadenadresse',
                            'Schadenart',
                            'Schadenhöhe',
                            'Anzahl Dateien',
                        ],
                    ],
                    data: claims.map((claim) => [
                        claim.contractNumber || '-',
                        claim.policyHolderName || '-',
                        claim.claimDate ? toHumanReadable(fromIso(claim.claimDate), 'date_short_2digit') : '-',
                        claim.incidentLocation || '-',
                        claim.claimType || '-',
                        claim.claimAmountEur ? formatEur(claim.claimAmountEur) : '-',
                        claim.filesCount.toString(),
                    ]),
                })}
            />
        </div>
    );
}

export function PendingClaims() {
    return (
        <PendingClaimsSelectionProvider>
            <PendingClaimsContent />
        </PendingClaimsSelectionProvider>
    );
}
